import { Track } from "@/src/lib/tracking";
import { getDeviceParser, getDeviceType, getItem } from "utils";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { getValue } from "@features/firebase/initFirebase";
import {
  REMOTE_CONFIG_KEY_APP_MINIMUM_BUILD_NUMBER,
  REMOTE_CONFIG_KEY_APP_UPDATE_RELEASE_NOTES,
  REMOTE_CONFIG_KEY_CAN_CLOSE_UPGRADE_MODAL,
} from "@features/firebase/remoteConfigKeys";
import type { AppInitData } from "@/src/lib/bridge/types";

const APP_VERSION_UPDATE_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

function parseRemoteMinBuild(
  configJson: string,
  platform: string,
): number | null {
  try {
    const config = JSON.parse(configJson) as Record<string, unknown>;
    const raw = config[platform];
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function getCurrentAppBuildNumber(): number | null {
  if (typeof window === "undefined") {
    return null;
  }
  const injected = (window as { __APP_DEVICE_INFO__?: AppInitData })
    .__APP_DEVICE_INFO__;
  const fromInjected = injected?.buildNumber;
  const fromUa = getDeviceParser().buildNumber;
  const raw = fromInjected ?? fromUa;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function needsAppVersionUpdate(
  currentBuild: number,
  remoteMinBuild: number,
): boolean {
  return currentBuild < remoteMinBuild;
}

function canShowAppVersionUpdateByInterval(
  lastShown: string | number | null | undefined,
  now: number,
  intervalMs: number = APP_VERSION_UPDATE_INTERVAL_MS,
): boolean {
  if (lastShown == null || lastShown === "") {
    return true;
  }
  const last = Number(lastShown);
  if (!Number.isFinite(last)) {
    return true;
  }
  return now - last >= intervalMs;
}

async function fetchRemoteMinBuildForPlatform(): Promise<number | null> {
  const platform = getDeviceType();
  if (platform !== "ios" && platform !== "android") {
    return null;
  }
  const configValue = (
    await getValue(REMOTE_CONFIG_KEY_APP_MINIMUM_BUILD_NUMBER)
  ).asString();
  return parseRemoteMinBuild(configValue, platform);
}

export async function fetchCanCloseUpgradeModal(): Promise<boolean> {
  try {
    const configValue = await getValue(
      REMOTE_CONFIG_KEY_CAN_CLOSE_UPGRADE_MODAL,
    );
    const raw = configValue.asString();
    if (!raw) {
      return true;
    }
    return configValue.asBoolean();
  } catch (error) {
    Track.log(
      "warn",
      "fetchCanCloseUpgradeModal",
      "failed, default allow close",
      { cause: String(error) },
    );
    return true;
  }
}

/**
 * 更新弹窗文案覆盖（RC `app_update_release_notes`）。两项都可缺，缺的用组件内置文案。
 *
 * ⚠️ 语义必须与原生 `kmp-app/shared/.../update/AppUpdateReleaseNotes.kt` 一致 ——
 * **同一条 RC 被两端读**，分叉了就会出现「App 里和 WebView 里说的话不一样」。
 */
export type AppUpdateCopyOverride = {
  title?: string;
  body?: string;
};

const RELEASE_NOTES_DEFAULT_SLOT_SUFFIX = "-default";

/**
 * 一个 JSON 值当且仅当是**非空字符串**时才算文案。
 *
 * 不用 `String(raw)` —— 那会把 `42` / `true` / `null` 也变成文案，
 * 控制台里手滑写成 `{"en-default": 42}` 就会把 "42" 渲染到强更弹窗上。
 */
function asCopyText(raw: unknown): string | undefined {
  if (typeof raw !== "string") {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed === "" ? undefined : raw;
}

/** 取一个槽。槽可以是对象 `{title, body}`，也可以是纯字符串（= 只给 body）。 */
function readSlot(
  root: Record<string, unknown>,
  candidateKeys: string[],
): AppUpdateCopyOverride {
  const wanted = new Set(candidateKeys.map((k) => k.toLowerCase()));
  const hit = Object.entries(root).find(([k]) => wanted.has(k.toLowerCase()));
  if (!hit) {
    return {};
  }
  const value = hit[1];
  if (typeof value === "string") {
    return { body: asCopyText(value) };
  }
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    return { title: asCopyText(obj.title), body: asCopyText(obj.body) };
  }
  return {};
}

/**
 * 解析 RC 原始串，取出当前语言的文案（覆盖槽优先，回落 `-default` 槽，逐字段独立）。
 *
 * 任何解析失败一律返回 `{}` ⇒ 用组件内置文案。**绝不抛** —— 这条 key 由人在控制台手写，
 * 格式错误必须降级，不能让更新弹窗因为一个多余的逗号变成空白正文（强更时它还关不掉）。
 *
 * 缺当前语言时**不跨语言回退**：回退到 en 会让繁中用户看到英文，
 * 而内置文案唯一强过 RC 的地方就是它一定是对的语言。
 */
export function parseAppUpdateReleaseNotes(
  rawJson: string,
  locale: string,
): AppUpdateCopyOverride {
  if (!rawJson || rawJson.trim() === "") {
    return {};
  }
  try {
    const root = JSON.parse(rawJson) as unknown;
    if (typeof root !== "object" || root === null || Array.isArray(root)) {
      return {};
    }
    const obj = root as Record<string, unknown>;
    const code = locale.toLowerCase();
    const override = readSlot(obj, [code]);
    const fallback = readSlot(obj, [code + RELEASE_NOTES_DEFAULT_SLOT_SUFFIX]);
    return {
      title: override.title ?? fallback.title,
      body: override.body ?? fallback.body,
    };
  } catch {
    return {};
  }
}

export async function fetchAppUpdateReleaseNotes(
  locale: string,
): Promise<AppUpdateCopyOverride> {
  try {
    const raw = (
      await getValue(REMOTE_CONFIG_KEY_APP_UPDATE_RELEASE_NOTES)
    ).asString();
    return parseAppUpdateReleaseNotes(raw, locale);
  } catch (error) {
    Track.log(
      "warn",
      "fetchAppUpdateReleaseNotes",
      "failed, fall back to built-in copy",
      { cause: String(error) },
    );
    return {};
  }
}

export type AppVersionUpdateModalDecision = {
  shouldShow: boolean;
  canCloseUpgradeModal: boolean;
};

export async function resolveAppVersionUpdateModal(
  now: number = Date.now(),
): Promise<AppVersionUpdateModalDecision> {
  const currentBuild = getCurrentAppBuildNumber();
  const [remoteMinBuild, canCloseUpgradeModal] = await Promise.all([
    fetchRemoteMinBuildForPlatform(),
    fetchCanCloseUpgradeModal(),
  ]);

  if (currentBuild == null || remoteMinBuild == null) {
    return { shouldShow: false, canCloseUpgradeModal };
  }
  if (!needsAppVersionUpdate(currentBuild, remoteMinBuild)) {
    return { shouldShow: false, canCloseUpgradeModal };
  }

  if (!canCloseUpgradeModal) {
    return { shouldShow: true, canCloseUpgradeModal: false };
  }

  const lastShown = getItem(
    LOCAL_STORAGE_KEYS.APP_VERSION_UPDATE_MODAL_LAST_SHOWN,
  ) as string | number | null | undefined;

  return {
    shouldShow: canShowAppVersionUpdateByInterval(lastShown, now),
    canCloseUpgradeModal: true,
  };
}

export async function shouldShowAppVersionUpdateModal(
  now: number = Date.now(),
): Promise<boolean> {
  const decision = await resolveAppVersionUpdateModal(now);
  return decision.shouldShow;
}
